import React from "react";
import { Link, useLocation } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useHelpVisibility } from "../hooks/useHelpVisibility";

/**
 * Bouton flottant "?" global — affiché sur les écrans principaux
 * (recherche, login, accueil, mon-ticket, mes-tickets, fiche commerçant…).
 * Détermine intelligemment vers quel centre d'aide pointer selon le contexte :
 *  - rôle "commerce"        → /help/establishment
 *  - rôle "client" ou anonyme → /help/visitor
 *  - admin                   → /admin/guides
 */
export default function FloatingHelpButton() {
  const loc = useLocation();
  const { role, user } = useAuth();
  const { visible } = useHelpVisibility();

  // Visibilité contrôlée par le toggle admin (default: caché)
  if (!visible) return null;

  // Routes où le bouton ne doit PAS apparaître (déjà sur une page d'aide,
  // ou contexte plein écran TV).
  const path = loc.pathname || "";
  const blockedExact = ["/tv"];
  const blockedPrefix = [
    "/help",
    "/admin",
    "/display/",
    "/payment/",
    "/q/",
  ];
  if (blockedExact.includes(path)) return null;
  if (blockedPrefix.some((p) => path.startsWith(p))) return null;

  let target = "/help/visitor";
  if (user?.isAdmin) target = "/admin/guides";
  else if (role === "commerce") target = "/help/establishment";

  return (
    <Link
      to={target}
      title="Centre d'aide"
      aria-label="Centre d'aide BypassGo"
      className="fixed bottom-5 right-5 z-30 w-12 h-12 rounded-full bg-[#002FA7] text-white shadow-lg hover:bg-[#001a87] hover:scale-105 transition-all flex items-center justify-center group"
      data-testid="floating-help-button"
    >
      <HelpCircle className="w-6 h-6" />
      <span className="hidden md:block absolute right-14 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
        Centre d'aide
      </span>
    </Link>
  );
}
